import { Eye, EyeOff } from 'lucide-react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { useId, useState } from 'react';

export interface FieldProps {
  label: ReactNode;
  required?: boolean;
  error?: string;
  children: (id: string, invalid: boolean) => ReactNode;
}

/** Label + control + error wrapper. Render-prop keeps any control usable. */
export function Field({ label, required, error, children }: FieldProps) {
  const id = useId();
  return (
    <div className="cg-field">
      <label className="cg-field__label" htmlFor={id}>
        {label}
        {required && <span className="cg-required">*</span>}
      </label>
      {children(id, Boolean(error))}
      {error && <span className="cg-field__error">{error}</span>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ invalid, className = '', ...rest }: InputProps) {
  return <input className={`cg-input ${className}`} aria-invalid={invalid || undefined} {...rest} />;
}

export interface UnitInputProps extends InputProps {
  unit: string;
}

/** Numeric input with trailing unit (e.g. 240 นาที) — settings pattern. */
export function UnitInput({ unit, style, ...rest }: UnitInputProps) {
  return (
    <span className="cg-input--suffix">
      <Input style={{ width: 90, ...style }} {...rest} />
      <span className="cg-input__unit">{unit}</span>
    </span>
  );
}

export interface PasswordInputProps extends InputProps {
  showLabel?: string;
  hideLabel?: string;
}

/** Password input with an eye visibility toggle (สร้างผู้ใช้ใหม่ pattern). */
export function PasswordInput({ showLabel = 'show', hideLabel = 'hide', ...rest }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="cg-input--password">
      <Input type={visible ? 'text' : 'password'} {...rest} />
      <button
        type="button"
        className="cg-input__eye"
        aria-label={visible ? hideLabel : showLabel}
        title={visible ? hideLabel : showLabel}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </span>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid, className = '', ...rest }: TextareaProps) {
  return <textarea className={`cg-textarea ${className}`} aria-invalid={invalid || undefined} {...rest} />;
}

/** Label-left / control-right row used across settings modals. */
export function FieldRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="cg-fieldrow">
      <span className="cg-fieldrow__label">{label}</span>
      {children}
    </div>
  );
}

/** Small bold section heading inside forms (e.g. ทักษะของผู้ให้บริการ). */
export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="cg-section-label">{children}</div>;
}

/** Save/cancel row under a form — consistent gap above and between buttons. */
export function FormActions({ children }: { children: ReactNode }) {
  return <div className="cg-form-actions">{children}</div>;
}
